// Purchase Orders data layer (Phase 4) — header + line items.
//
// The SINGLE interface between the app and the purchase-order schema
// (supabase/migrations/20260630020040_purchase_orders.sql).
//
// SECURITY MODEL (enforced server-side; mirrored here):
//   • We NEVER send company_id or po_number — DB triggers own them
//     (company forced/inherited; po_number auto = PO-00001; both immutable).
//   • Line items inherit company_id from their parent PO via trigger.
//   • RLS rejects anything out of company scope.
//
// RETURN-TYPE CONTRACT (consistent with products/suppliers):
//   • Collection reads → { data, count, page?, pageSize? }
//   • Single read      → T | null   (header with embedded supplier + lines)
//   • create / update  → the affected id (string)
//   • delete           → void
//   • All functions THROW a mapped Error on failure.

import { supabase } from "./supabaseClient";
import { C } from "../customers/theme";

const PO = "purchase_orders";
const LINES = "purchase_order_lines";

/** Default rows per page. */
export const DEFAULT_PAGE_SIZE = 25;

/** Colour-coded statuses (for badges + the DB CHECK). */
export const PO_STATUSES = [
  { value: "draft", label: "Draft", color: C.textMuted },
  { value: "sent", label: "Sent", color: C.primary },
  { value: "partially_received", label: "Partially Received", color: C.warning },
  { value: "received", label: "Received", color: C.success },
  { value: "cancelled", label: "Cancelled", color: C.danger },
];

export const PAYMENT_TERMS = [
  { value: "due_on_receipt", label: "Due on receipt" },
  { value: "net_7", label: "Net 7 days" },
  { value: "net_14", label: "Net 14 days" },
  { value: "net_30", label: "Net 30 days" },
  { value: "net_60", label: "Net 60 days" },
  { value: "net_90", label: "Net 90 days" },
];

export const CURRENCIES = [
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
  { value: "USD", label: "USD ($)" },
];

export const PO_SORTS = [
  { value: "number", label: "PO No." },
  { value: "order_date", label: "Order Date" },
  { value: "status", label: "Status" },
  { value: "total", label: "Total" },
  { value: "created", label: "Newest" },
];

const HEADER_WRITE_FIELDS = [
  "supplier_id", "supplier_contact", "supplier_email", "order_date",
  "expected_delivery_date", "status", "preferred_currency", "payment_terms",
  "notes", "internal_notes",
];
const LINE_WRITE_FIELDS = ["product_id", "sku", "description", "quantity", "unit_cost", "discount_percent", "vat_rate"];
const SORT_COLUMNS = { number: "po_number", order_date: "order_date", status: "status", total: "total_amount", created: "created_at" };

const HEADER_SELECT =
  "id, po_number, supplier_id, supplier_contact, supplier_email, order_date, expected_delivery_date, " +
  "status, preferred_currency, payment_terms, subtotal, total_discount, total_vat, total_amount, created_at, updated_at, " +
  "suppliers ( id, company_name )";
const FULL_SELECT =
  "*, suppliers ( id, company_name, email, phone, contact_name, payment_terms ), " +
  "purchase_order_lines ( id, product_id, sku, description, quantity, unit_cost, discount_percent, vat_rate, line_total, qty_received, position )";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
const sanitizeTerm = (t) => String(t || "").trim().replace(/[%,()]/g, "");
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23514": message = "Invalid value — check the status field."; break;
    case "23503": message = "Referenced supplier or product does not exist."; break;
    case "23502": message = "A required field is missing."; break;
    case "PGRST116": message = "Purchase order not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// ── Totals (single source of truth, used by the form preview AND on save) ─────

const lineNet = (l) => (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0) * (1 - (Number(l.discount_percent) || 0) / 100);

/**
 * Compute per-line and document totals from line items.
 * line_total = net (qty · unit_cost · (1 − discount%)); VAT excluded.
 * @param {Array} lines
 * @returns {{ lines: Array, subtotal: number, total_discount: number, total_vat: number, total_amount: number }}
 */
export function computeTotals(lines = []) {
  let subtotal = 0, total_discount = 0, total_vat = 0;
  const computed = lines.map((l, i) => {
    const net = lineNet(l);
    const gross = (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0);
    subtotal += net;
    total_discount += gross - net;
    total_vat += net * ((Number(l.vat_rate) || 0) / 100);
    return { ...l, line_total: round2(net), position: i };
  });
  return {
    lines: computed,
    subtotal: round2(subtotal),
    total_discount: round2(total_discount),
    total_vat: round2(total_vat),
    total_amount: round2(subtotal + total_vat),
  };
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * List purchase orders (RLS-scoped) with search, status/supplier filters,
 * sorting and pagination. Embeds the supplier name.
 * @param {Object} [opts]
 * @param {string} [opts.search]      ILIKE over po_number, supplier_contact, supplier_email.
 * @param {"all"|string} [opts.status]
 * @param {string} [opts.supplierId]
 * @param {number} [opts.page=1]
 * @param {number} [opts.pageSize=25]
 * @param {"number"|"order_date"|"status"|"total"|"created"} [opts.sortBy="created"]
 * @param {"asc"|"desc"} [opts.sortDir="desc"]
 * @returns {Promise<{data:Array,count:number,page:number,pageSize:number}>}
 * @throws {Error}
 */
export async function getPurchaseOrders(opts = {}) {
  const { search, status, supplierId, page = 1, pageSize = DEFAULT_PAGE_SIZE, sortBy = "created", sortDir = "desc" } = opts;
  const c = client();
  const column = SORT_COLUMNS[sortBy] || "created_at";
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const from = (safePage - 1) * safeSize;
  const to = from + safeSize - 1;

  let q = c.from(PO).select(HEADER_SELECT, { count: "exact" }).order(column, { ascending: sortDir !== "desc" }).range(from, to);
  if (status && status !== "all") q = q.eq("status", status);
  if (supplierId && supplierId !== "all") q = q.eq("supplier_id", supplierId);
  const term = sanitizeTerm(search);
  if (term) q = q.or(`po_number.ilike.%${term}%,supplier_contact.ilike.%${term}%,supplier_email.ilike.%${term}%`);

  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load purchase orders");
  return { data: data || [], count: count ?? 0, page: safePage, pageSize: safeSize };
}

/**
 * Convenience search wrapper.
 * @param {string} term @param {Object} [opts] @returns {Promise<{data:Array,count:number}>}
 */
export async function searchPurchaseOrders(term, opts = {}) {
  return getPurchaseOrders({ ...opts, search: term });
}

/**
 * Fetch a full purchase order (header + supplier + lines, ordered).
 * @param {string} id
 * @returns {Promise<Object|null>}
 * @throws {Error}
 */
export async function getPurchaseOrder(id) {
  const { data, error } = await client().from(PO).select(FULL_SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load purchase order");
  if (data?.purchase_order_lines) data.purchase_order_lines.sort((a, b) => (a.position || 0) - (b.position || 0));
  return data || null;
}

// ── Writes ───────────────────────────────────────────────────────────────────

async function replaceLines(poId, lines) {
  const c = client();
  const { error: delErr } = await c.from(LINES).delete().eq("purchase_order_id", poId);
  if (delErr) throw toError(delErr, "Could not update line items");
  const rows = (lines || [])
    .filter((l) => l.product_id || (l.description && l.description.trim()) || Number(l.quantity) > 0)
    .map((l, i) => ({ ...pick(l, LINE_WRITE_FIELDS), line_total: round2(lineNet(l)), position: i, purchase_order_id: poId }));
  if (rows.length) {
    const { error } = await c.from(LINES).insert(rows);
    if (error) throw toError(error, "Could not save line items");
  }
}

/**
 * Create a purchase order with its lines. company_id and po_number are assigned
 * server-side; totals are computed here from the lines.
 * @param {Object} payload  Header fields + `lines: Array`.
 * @returns {Promise<string>} the new PO id.
 * @throws {Error}
 */
export async function createPurchaseOrder(payload) {
  const c = client();
  const totals = computeTotals(payload?.lines || []);
  const headerRow = {
    ...pick(payload || {}, HEADER_WRITE_FIELDS),
    subtotal: totals.subtotal, total_discount: totals.total_discount, total_vat: totals.total_vat, total_amount: totals.total_amount,
  };
  const { data, error } = await c.from(PO).insert(headerRow).select("id").single();
  if (error) throw toError(error, "Could not create purchase order");
  await replaceLines(data.id, totals.lines);
  return data.id;
}

/**
 * Update a purchase order header + replace its lines. company_id/po_number immutable.
 * @param {string} id @param {Object} payload  Header fields + `lines: Array`.
 * @returns {Promise<string>} the PO id.
 * @throws {Error}
 */
export async function updatePurchaseOrder(id, payload) {
  const c = client();
  const totals = computeTotals(payload?.lines || []);
  const headerRow = {
    ...pick(payload || {}, HEADER_WRITE_FIELDS),
    subtotal: totals.subtotal, total_discount: totals.total_discount, total_vat: totals.total_vat, total_amount: totals.total_amount,
  };
  const { error } = await c.from(PO).update(headerRow).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not update purchase order");
  await replaceLines(id, totals.lines);
  return id;
}

/**
 * Delete a purchase order (cascades its lines). No-op if not visible to the caller.
 * @param {string} id @returns {Promise<void>} @throws {Error}
 */
export async function deletePurchaseOrder(id) {
  const { error } = await client().from(PO).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete purchase order");
}
